import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, Plus, Trash2, FileText, Mail, Loader2, Building2, CalendarClock, StickyNote } from "lucide-react";
import { CvShell } from "@/components/cv/CvShell";
import { CvGate } from "@/components/cv/CvGate";
import { useAuth } from "@/context/AuthContext";
import { cvApi } from "@/lib/api";

const STATUS_STYLE = {
  do_wyslania: "border-brand-line text-brand-sec",
  wyslana: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  rozmowa: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  oferta: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  odrzucona: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function CvApplications() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ company: "", role: "" });
  const [saving, setSaving] = useState(false);
  const timers = useRef({});

  const isDue = (d) => {
    if (!d) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return new Date(d) <= today;
  };

  const updateField = (id, key, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [key]: value } : it)));
    const k = `${id}:${key}`;
    clearTimeout(timers.current[k]);
    timers.current[k] = setTimeout(async () => {
      try { await cvApi.appPatch(id, { [key]: value }); } catch { /* best-effort */ }
    }, 700);
  };

  const load = () => {
    cvApi.appList().then((d) => { setItems(d.items || []); setStatuses(d.statuses || []); }).catch(() => setItems([]));
  };
  useEffect(() => { if (user) load(); }, [user]);

  const createApp = async () => {
    if (!form.company.trim() && !form.role.trim()) { toast.error("Podaj firmę lub stanowisko."); return; }
    setSaving(true);
    try {
      await cvApi.appCreate({ company: form.company, role: form.role });
      setForm({ company: "", role: "" });
      setAdding(false);
      load();
      toast.success("Aplikacja dodana.");
    } catch {
      toast.error("Nie udało się dodać aplikacji.");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const updated = await cvApi.appPatch(id, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
    } catch {
      toast.error("Nie udało się zmienić statusu.");
    }
  };

  const removeApp = async (id) => {
    try {
      await cvApi.appDelete(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Aplikacja usunięta.");
    } catch {
      toast.error("Nie udało się usunąć aplikacji.");
    }
  };

  const inputCls = "w-full rounded-button border border-brand-line bg-brand-bg px-3 py-2.5 text-sm text-brand-text focus:border-brand-accent focus:outline-none";

  return (
    <CvShell active="aplikacje">
      <div data-testid="cv-applications" className="pb-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-2xl font-semibold text-brand-text sm:text-3xl">Moje aplikacje</h1>
          {user && (
            <button type="button" data-testid="cv-app-add-toggle" onClick={() => setAdding((v) => !v)} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5">
              <Plus size={16} /> Dodaj aplikację
            </button>
          )}
        </div>
        <p className="mt-2 max-w-2xl text-brand-sec">Śledź, do kogo aplikujesz i na jakim etapie jesteś. Status aktualizujesz ręcznie — nie wysyłamy wiadomości automatycznie.</p>

        {!user ? (
          <div className="mt-8 max-w-2xl"><CvGate returnTo="/cv/aplikacje" message="Załóż bezpłatne konto, aby zapisywać i śledzić swoje aplikacje." /></div>
        ) : (
          <>
            {adding && (
              <div className="mt-5 rounded-panel border border-brand-line bg-brand-surface p-4" data-testid="cv-app-form">
                <div className="grid gap-3 sm:grid-cols-2">
                  <input data-testid="cv-app-company" className={inputCls} placeholder="Firma / organizacja" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  <input data-testid="cv-app-role" className={inputCls} placeholder="Stanowisko" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
                </div>
                <div className="mt-3 flex gap-2">
                  <button type="button" data-testid="cv-app-save" disabled={saving} onClick={createApp} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-4 py-2 text-sm font-semibold text-brand-on disabled:opacity-50">
                    {saving ? <Loader2 size={15} className="animate-spin" /> : null} Zapisz
                  </button>
                  <button type="button" onClick={() => setAdding(false)} className="rounded-button border border-brand-line px-4 py-2 text-sm font-semibold text-brand-text">Anuluj</button>
                </div>
              </div>
            )}

            {items === null ? (
              <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-brand-accent" /></div>
            ) : items.length === 0 ? (
              <div className="mt-6 flex flex-col items-center justify-center gap-3 rounded-panel border border-dashed border-brand-line px-6 py-16 text-center" data-testid="cv-applications-empty">
                <Inbox size={34} className="text-brand-line" />
                <p className="text-brand-text">Brak zapisanych aplikacji.</p>
                <p className="max-w-md text-sm text-brand-muted">Dodaj aplikację ręcznie lub użyj „Zapisz jako aplikację" w kreatorze listu motywacyjnego.</p>
              </div>
            ) : (
              <ul className="mt-6 space-y-3" data-testid="cv-applications-list">
                {items.map((it) => (
                  <li key={it.id} data-testid={`cv-app-item-${it.id}`} className="rounded-panel border border-brand-line bg-brand-surface p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 font-medium text-brand-text"><Building2 size={16} className="shrink-0 text-brand-accent" /> {it.company || "—"}</p>
                        <p className="mt-0.5 text-[13.5px] text-brand-sec">{it.role || "—"}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] text-brand-muted">
                          {it.cv_title && <span>CV: {it.cv_title}</span>}
                          {it.cv_id && <Link to={`/cv/kreator/${it.cv_id}`} className="inline-flex items-center gap-1 text-brand-accent hover:underline"><FileText size={12} /> Otwórz CV</Link>}
                          {it.letter_id && <Link to={`/cv/list/${it.letter_id}`} className="inline-flex items-center gap-1 text-brand-accent hover:underline"><Mail size={12} /> Otwórz list</Link>}
                          <span>· {new Date(it.updated_at).toLocaleDateString("pl-PL")}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          data-testid={`cv-app-status-${it.id}`}
                          value={it.status}
                          onChange={(e) => changeStatus(it.id, e.target.value)}
                          className={`rounded-full border px-3 py-1.5 text-[12.5px] font-semibold ${STATUS_STYLE[it.status] || "border-brand-line text-brand-sec"}`}
                        >
                          {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                        <button type="button" data-testid={`cv-app-delete-${it.id}`} onClick={() => removeApp(it.id)} className="rounded-button border border-brand-line p-2 text-red-400 hover:border-red-400/60"><Trash2 size={15} /></button>
                      </div>
                    </div>
                    {/* Notatka i przypomnienie */}
                    <div className="mt-3 grid gap-3 border-t border-brand-line pt-3 sm:grid-cols-[1fr_auto]">
                      <div className="flex items-start gap-2">
                        <StickyNote size={15} className="mt-2 shrink-0 text-brand-muted" />
                        <textarea
                          data-testid={`cv-app-note-${it.id}`}
                          rows={1}
                          value={it.note || ""}
                          onChange={(e) => updateField(it.id, "note", e.target.value)}
                          placeholder="Notatka (np. kontakt, wymagania, ustalenia)…"
                          className="w-full resize-y rounded-button border border-brand-line bg-brand-bg px-3 py-2 text-[13px] text-brand-text focus:border-brand-accent focus:outline-none"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarClock size={15} className={`shrink-0 ${isDue(it.follow_up) ? "text-amber-400" : "text-brand-muted"}`} />
                        <input
                          type="date"
                          data-testid={`cv-app-followup-${it.id}`}
                          value={(it.follow_up || "").slice(0, 10)}
                          onChange={(e) => updateField(it.id, "follow_up", e.target.value)}
                          className={`rounded-button border bg-brand-bg px-3 py-2 text-[13px] text-brand-text focus:border-brand-accent focus:outline-none ${isDue(it.follow_up) ? "border-amber-500/50" : "border-brand-line"}`}
                        />
                        {isDue(it.follow_up) && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-300">Przypomnienie</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </CvShell>
  );
}
